/**
 * Accessibility plumbing shared by every screen.
 *
 * - `focusFirstInvalid(form)` — what an error summary does when the user activates it.
 * - `announce(message)` — polite screen-reader speech through ONE live region.
 * - `useRestoreFocus(active)` — put focus back where it was. Radix already does this for
 *   Dialog / Sheet / AlertDialog; this is for the non-Radix cases (an inline editor, a
 *   row that collapses).
 */
import * as React from "react";

/** A control the browser or our own validation has marked as wrong. */
const INVALID_SELECTOR =
  '[aria-invalid="true"]:not([disabled]), [aria-invalid=""]:not([disabled]), :invalid:not([disabled])';

/**
 * Focuses the first field in `form` that is marked wrong and returns it.
 * Returns null when nothing is wrong, so a caller can skip the summary entirely.
 */
export function focusFirstInvalid(formEl: HTMLElement | null | undefined): HTMLElement | null {
  if (!formEl) return null;
  const first = formEl.querySelector<HTMLElement>(INVALID_SELECTOR);
  if (!first) return null;
  // A wrapper can carry aria-invalid; focus the control inside it if so.
  const target = first.matches("input, select, textarea, button, [tabindex]")
    ? first
    : (first.querySelector<HTMLElement>("input, select, textarea, button, [tabindex]") ?? first);
  target.focus({ preventScroll: true });
  target.scrollIntoView({ block: "center", behavior: "auto" });
  return target;
}

// --- the single live region -------------------------------------------------

/** Written out, not typed in: prettier turns the escape into a real character. */
const ZERO_WIDTH_SPACE = String.fromCharCode(0x200b);

type Listener = () => void;
let currentMessage = "";
let version = 0;
const listeners = new Set<Listener>();

/**
 * Says something politely to a screen reader. Safe to call before <LiveRegion/> mounts:
 * the latest message is kept and spoken once the region appears.
 */
export function announce(message: string): void {
  // Repeating the same string would not change the DOM, so nothing would be read out.
  // A trailing zero-width space alternates the text without changing what is heard.
  version += 1;
  currentMessage = version % 2 === 0 ? message + ZERO_WIDTH_SPACE : message;
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => currentMessage;

/** Exported for tests only. */
export function resetAnnouncements(): void {
  currentMessage = "";
  version = 0;
  for (const listener of listeners) listener();
}

/**
 * Mount this ONCE, in the app shell. Every `announce()` in the product speaks through it.
 * Two regions would read every message twice.
 */
export function LiveRegion(): React.ReactElement {
  const message = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return React.createElement(
    "div",
    {
      "aria-live": "polite",
      "aria-atomic": "true",
      "data-slot": "live-region",
      className: "sr-only",
    },
    message,
  );
}

// --- focus restoration ------------------------------------------------------

/**
 * While `active` is true, remembers what had focus when it turned true and returns focus
 * there when it turns false or the component unmounts.
 */
export function useRestoreFocus(active: boolean): void {
  const previous = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!active) return;
    previous.current = document.activeElement as HTMLElement | null;
    return () => {
      const el = previous.current;
      previous.current = null;
      // The old element can have left the DOM: focusing it then would throw away focus.
      if (el && typeof el.focus === "function" && el.isConnected) el.focus({ preventScroll: true });
    };
  }, [active]);
}
